import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CS_TEL_HREF, CS_PHONE_DISPLAY } from "@/brand";
import { Camera, Sparkles, ChevronLeft, ChevronRight, MapPin, Calendar, Download, X as XIcon, Package } from "lucide-react";
import { API, BACKEND_URL, LOGO_URL } from "@/config";
import { Fireflies } from "@/components/effects/Fireflies";

/**
 * Public photo confirmation page. Customers land here via the delivery SMS /
 * WhatsApp / email ("Enchanted Gear delivered your items ✨ Photo confirmation:
 * …/photos/{id}") and see the pictures the driver snapped at drop-off.
 *
 * Enrichments over the original card:
 *   - Delivery location (Bell Desk / Hotel Lobby / etc.) called out on top
 *   - Full itemized "Delivered" list so guest sees exactly what was left
 *   - Download-all button that hits the /photos.zip endpoint
 *   - Lightbox with left/right navigation and keyboard shortcuts
 */
export const PhotoGallery = () => {
  const { reservationId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/reservations/${reservationId}/photos-public`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Photos not found.");
      }
    })();
    return () => { cancelled = true; };
  }, [reservationId]);

  const delivery = (data?.photos || []).filter((p) => p.kind === "delivery");
  const returns = (data?.photos || []).filter((p) => p.kind === "return");
  const allPhotos = [...delivery, ...returns];

  const closePreview = useCallback(() => setPreviewIdx(null), []);
  const prev = useCallback(() => setPreviewIdx((i) => (i === null ? null : (i - 1 + allPhotos.length) % allPhotos.length)), [allPhotos.length]);
  const next = useCallback(() => setPreviewIdx((i) => (i === null ? null : (i + 1) % allPhotos.length)), [allPhotos.length]);

  useEffect(() => {
    if (previewIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewIdx, closePreview, prev, next]);

  const downloadAll = () => {
    // Direct link — the backend streams a zip named after the short_code.
    // No JS blob juggling; the browser handles the download normally.
    window.location.href = `${API}/reservations/${reservationId}/photos.zip`;
  };

  const deliveredAt = data?.reservation?.delivered_at
    ? new Date(data.reservation.delivered_at).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : null;

  return (
    <div className="min-h-screen bg-[#04090F] no-custom-cursor relative overflow-hidden">
      <Fireflies count={14} />
      <div className="relative z-10">
        <header className="border-b border-[#FFD166]/15 bg-[#0A1A26]/60 backdrop-blur">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2 text-[#FFD166] hover:text-[#F4A93A] text-sm shrink-0" data-testid="photos-home-link">
              <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Enchanted Gear Rentals</span>
            </Link>
            <img src={LOGO_URL} alt="Enchanted Gear Rentals" className="h-9 sm:h-10" style={{ filter: "url(#knockoutBlack)" }} />
            <div className="w-24 text-right text-[10px] uppercase tracking-widest text-[#B9C8D5] hidden sm:block">Delivery Proof</div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
          {err ? (
            <div className="glass p-8 text-center" data-testid="photos-error">
              <h1 className="font-serif text-3xl text-[#FFD166] mb-3">Photos not available</h1>
              <p className="text-sm text-[#B9C8D5]">{err}</p>
            </div>
          ) : !data ? (
            <div className="glass p-10 text-center" data-testid="photos-loading">
              <p className="font-serif text-2xl text-[#FFD166]">Loading your photos…</p>
            </div>
          ) : (
            <>
              {/* Hero header — the customer greeting */}
              <div className="mb-8" data-testid="photos-heading">
                <div className="flex items-center gap-2 text-[#FFD166] text-xs uppercase tracking-widest mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Delivered with care
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl text-[#FFD166] mb-2">
                  Hi {data.reservation.customer_name?.split(" ")[0] || "friend"} —
                </h1>
                <p className="text-[#F6F1E4] text-base sm:text-lg">
                  Here's proof your gear was dropped off safe and sound.
                </p>
              </div>

              {/* Delivery metadata card — location + when + short_code */}
              <div className="glass p-5 mb-8 space-y-3" data-testid="photos-meta">
                {data.reservation.delivery_resort && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#FFD166] mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Location</div>
                      <div className="text-[#F6F1E4]">{data.reservation.delivery_resort}</div>
                      {data.reservation.delivery_location_label && (
                        <div className="text-[#FFD166] text-xs mt-0.5">Left at: {data.reservation.delivery_location_label}</div>
                      )}
                    </div>
                  </div>
                )}
                {deliveredAt && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-[#FFD166] mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Delivered</div>
                      <div className="text-[#F6F1E4]">{deliveredAt}</div>
                    </div>
                  </div>
                )}
                {(data.reservation.line_items || []).length > 0 && (
                  <div className="flex items-start gap-3">
                    <Package className="w-4 h-4 text-[#FFD166] mt-0.5 shrink-0" />
                    <div className="text-sm w-full">
                      <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Delivered gear</div>
                      <ul className="text-[#F6F1E4] mt-1 space-y-0.5" data-testid="photos-items">
                        {data.reservation.line_items.map((li) => (
                          <li key={li.product_id}>× {li.quantity} — {li.name}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {data.reservation.short_code && (
                  <div className="pt-3 mt-1 border-t border-[#FFD166]/15 text-[10px] text-[#B9C8D5] font-mono">
                    Reservation {data.reservation.short_code}
                  </div>
                )}
              </div>

              {/* Photo sections + download */}
              {allPhotos.length > 0 && (
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div className="text-xs text-[#B9C8D5]">
                    <span className="text-[#F6F1E4] font-medium">{allPhotos.length}</span> photo{allPhotos.length === 1 ? "" : "s"} available
                  </div>
                  <button
                    type="button"
                    onClick={downloadAll}
                    className="inline-flex items-center gap-2 text-sm bg-[#FFD166] hover:bg-[#F4A93A] text-[#040A0F] font-medium px-5 py-2 rounded-full transition-colors shadow-sm"
                    data-testid="photos-download-all-btn"
                  >
                    <Download className="w-3.5 h-3.5" /> Download all photos
                  </button>
                </div>
              )}

              <Section title="Drop-off" photos={delivery} onOpen={(p) => setPreviewIdx(allPhotos.findIndex((x) => x.id === p.id))} testid="delivery" empty="No drop-off photos yet — refresh this page once your driver uploads them." />
              {returns.length > 0 && (
                <Section title="Return check" photos={returns} onOpen={(p) => setPreviewIdx(allPhotos.findIndex((x) => x.id === p.id))} testid="return" />
              )}

              <div className="glass-warm mt-10 p-5 text-center text-sm text-[#F6F1E4]">
                <p className="mb-2">Questions? Call or text us at <a href={CS_TEL_HREF} className="text-[#FFD166] hover:underline" data-testid="gallery-cs-phone">{CS_PHONE_DISPLAY}</a> — we're here all day.</p>
                <p className="text-[10px] text-[#B9C8D5]">Enchanted Gear Rentals ✨ Near Walt Disney World</p>
              </div>
            </>
          )}
        </main>
      </div>

      {previewIdx !== null && allPhotos[previewIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closePreview(); }}
            className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white rounded-full p-2"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
          {allPhotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-4 sm:left-8 bg-black/60 hover:bg-black/80 text-white rounded-full p-3"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-4 sm:right-8 bg-black/60 hover:bg-black/80 text-white rounded-full p-3"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <img
            src={`${BACKEND_URL}${allPhotos[previewIdx].url}`}
            alt=""
            className="max-w-full max-h-[92vh] rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-xs bg-black/60 px-3 py-1 rounded-full">
            {previewIdx + 1} / {allPhotos.length} · {allPhotos[previewIdx].kind === "delivery" ? "Drop-off" : "Return"}
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, photos, onOpen, testid, empty }) => (
  <div className="mb-8" data-testid={`photos-section-${testid}`}>
    <div className="flex items-center gap-2 mb-3">
      <Camera className="w-4 h-4 text-[#FFD166]" />
      <h2 className="font-serif text-2xl text-[#FFD166]">{title}</h2>
    </div>
    {photos.length === 0 ? (
      <p className="text-sm text-[#B9C8D5] italic">{empty}</p>
    ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpen(p)}
            className="aspect-square rounded-xl overflow-hidden border border-[#FFD166]/25 bg-black/40 group relative"
            data-testid={`photos-thumb-${p.id}`}
          >
            <img src={`${BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          </button>
        ))}
      </div>
    )}
  </div>
);

export default PhotoGallery;
